import * as React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

interface ConfidenceIndicatorProps {
  /**
   * Confidence score between 0 and 1
   */
  score: number;
  /**
   * Optional label to display next to the indicator
   */
  label?: string;
  /**
   * Size of the indicator
   */
  size?: "small" | "medium";
}

/**
 * Displays a color-coded confidence indicator based on the score
 * - Red (0-0.6): Low confidence
 * - Yellow (0.6-0.85): Medium confidence
 * - Green (0.85-1.0): High confidence
 */
function ConfidenceIndicator({
  score,
  label,
  size = "small",
}: ConfidenceIndicatorProps) {
  // Determine color based on confidence score
  const getColor = (score: number): "error" | "warning" | "success" => {
    if (score < 0.6) return "error";
    if (score < 0.85) return "warning";
    return "success";
  };

  // Format score as percentage
  const percentage = Math.round(score * 100);

  // Get color label for tooltip
  const getConfidenceLevel = (score: number): string => {
    if (score < 0.6) return "Low";
    if (score < 0.85) return "Medium";
    return "High";
  };

  const color = getColor(score);
  const confidenceLevel = getConfidenceLevel(score);

  return (
    <Tooltip title={`${confidenceLevel} confidence: ${percentage}%`}>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        {label && (
          <Box
            component="span"
            sx={{
              fontSize: size === "small" ? "0.875rem" : "1rem",
              color: "text.secondary",
            }}
          >
            {label}:
          </Box>
        )}
        <Chip
          label={`${percentage}%`}
          color={color}
          size={size}
          sx={{
            fontWeight: 600,
            minWidth: size === "small" ? "60px" : "70px",
          }}
        />
      </Box>
    </Tooltip>
  );
}

export default ConfidenceIndicator;
